import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { normaliseCoupons, sortCoupons } from '@mypoz/shared';
import { useLocale } from '@mypoz/i18n';

import { CouponsContext, type CouponsState } from './context.js';
import { fetchCouponRows } from './api.js';

/**
 * Every coupon on the account, fetched once (MYP-73).
 *
 * Structured after `PackagesProvider`: raw rows in state, normalisation in a `useMemo`, so
 * switching language re-resolves the coupon type labels already in memory rather than asking the
 * vendor for data that did not change.
 *
 * No status dictionary to fetch alongside it, unlike Packages and Orders — a coupon's state is
 * derived here rather than labelled by the vendor, for the reason `coupons-wire.ts` sets out.
 */
export function CouponsProvider({ children }: { children: ReactNode }) {
  const { locale } = useLocale();
  const [rows, setRows] = useState<readonly unknown[] | undefined>(undefined);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    // Guards StrictMode's double-mount and a reload pressed mid-flight alike: only the newest
    // effect's result lands.
    let current = true;

    setRows(undefined);
    setFailed(false);

    fetchCouponRows().then(
      (next) => {
        if (!current) return;
        setRows(next);
      },
      (cause: unknown) => {
        if (!current) return;
        // Logged, not shown: the vendor's failure text is Chinese prose from its own code table.
        console.error('[coupons] could not load the coupon list', cause);
        setFailed(true);
      },
    );

    return () => {
      current = false;
    };
  }, [attempt]);

  const reload = useCallback(() => setAttempt((previous) => previous + 1), []);

  const value = useMemo<CouponsState>(() => {
    // ms reads the English side — the vendor publishes no Malay label, the same compromise
    // `PackagesProvider` documents for its status dictionary.
    const coupons = rows ? normaliseCoupons(rows, locale === 'zh' ? 'zh' : 'en') : [];

    return {
      // One clock for the whole list, so nothing in a render disagrees about what "now" is.
      coupons: sortCoupons(coupons, new Date()),
      status: rows ? 'ready' : failed ? 'error' : 'loading',
      reload,
    };
  }, [rows, failed, locale, reload]);

  return <CouponsContext.Provider value={value}>{children}</CouponsContext.Provider>;
}
